import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FinecoDownloadButton } from "pages/assistant/components/FinecoDownloadButton";
import { AssistantService } from "shared/services/AssistantService";
import { useToast } from "shared/components/ui/use-toast";
import styles from "pages/assistant/components/artifacts/components/fineco-portfolio/finecoPortfolio.common.module.scss";
import { createLogger } from "shared/logger";

const log = createLogger("ProductCardCompareButtons");

type FinecoProductCardCompareButtonsProps = {
  isins: string[];
};

export const FinecoProductCardCompareButtons: React.FC<FinecoProductCardCompareButtonsProps> = ({
  isins,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [generatingPdf, setGeneratingPdf] = useState<boolean>(false);

  const handleDownloadProductCard = useCallback(async () => {
    if (!isins || isins.length === 0) {
      toast({
        description: "No ISINs available for comparison",
        title: t("global.error"),
        variant: "destructive",
      });
      return;
    }

    setGeneratingPdf(true);
    try {
      const response = await AssistantService.createProductCardComparePdf(isins);

      const blob = new Blob([response], { type: "application/pdf" });
      if (blob.type === "application/pdf") {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `product-card-compare-${isins.join('-')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast({
          description: t("productCard.downloadedSuccess"),
          title: t("global.success"),
        });
      } else {
        toast({
          description: t("productCard.downloadError"),
          title: t("global.error"),
          variant: "destructive",
        });
      }
    } catch (error) {
      log.error("product card comparison PDF generation failed", { error });
      // Always the translated message: the raw error text is English (e.g.
      // axios «Network Error»). The global toast is suppressed in AssistantService.
      toast({
        description: t("productCard.downloadError"),
        title: t("global.error"),
        variant: "destructive",
      });
    } finally {
      setGeneratingPdf(false);
    }
  }, [isins, t, toast]);

  return (
    <div className={styles.buttonContainer}>
      <FinecoDownloadButton
        click={handleDownloadProductCard}
        copy={
          !generatingPdf
            ? t("productCard.downloadProductCardCompare")
            : t("productCard.downloadingProductCard")
        }
        disabled={generatingPdf}
      />
    </div>
  );
}; 